import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Check, Pencil, Trash2, ImagePlus } from 'lucide-react';
import type { GoalCheckin, GoalDetail } from '@techsofit/api-types';
import { ApiClientError, mediaUrl } from '@techsofit/app-kit';
import { Button, Card, Input, Textarea, Modal, Skeleton, EmptyState, useConfirm, cn } from '@techsofit/ui';
import { useAsync } from '../lib/useAsync';
import { getGoal, recordCheckin, updateGoal, deleteCheckin, uploadCheckinPhoto } from '../lib/gymApi';
import { GOAL_STATUS_META, valueLabel, cadenceLabel, metricLabel } from '../lib/goals';
import { formatDate, todayIso } from '../lib/format';

export function GoalDetailPage() {
  const { goalId = '' } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data, loading, error, reload } = useAsync<GoalDetail>(() => getGoal(goalId), [goalId]);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  if (loading) {
    return (
      <section className="animate-fade-up">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mt-4 h-48" />
        <Skeleton className="mt-3 h-64" />
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className="animate-fade-up">
        <BackLink onClick={() => navigate(-1)} />
        <EmptyState title="Goal not found" description={error ?? 'It may have been removed.'} />
      </section>
    );
  }

  const { goal, checkins } = data;
  const meta = GOAL_STATUS_META[goal.status];
  const canCheckIn = goal.status !== 'ARCHIVED';

  async function setStatus(status: GoalDetail['goal']['status']) {
    await updateGoal(goalId, { status });
    reload();
  }

  async function onArchive() {
    const ok = await confirm({
      title: `Archive “${goal.title}”?`,
      description: 'It will stop sending reminders and move out of the active list. You can reactivate it later.',
      confirmLabel: 'Archive',
      tone: 'danger',
    });
    if (ok) setStatus('ARCHIVED');
  }

  return (
    <section className="animate-fade-up">
      <BackLink onClick={() => navigate(`/members/${goal.memberId}`)} label={goal.memberName} />

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-display-md text-ink">{goal.title}</h1>
            <span className={cn('rounded-pill px-2.5 py-1 text-xs font-medium', meta.cls)}>{meta.label}</span>
          </div>
          <p className="mt-1 text-sm text-muted">
            {metricLabel(goal.metric)} · {cadenceLabel(goal.cadenceDays)}
            {goal.coachName && ` · Coach ${goal.coachName}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCheckIn && (
            <Button onClick={() => setCheckinOpen(true)}>
              <Plus className="size-4" aria-hidden />
              <span className="hidden sm:inline">Add check-in</span>
            </Button>
          )}
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="size-4" aria-hidden />
            <span className="hidden sm:inline">Edit</span>
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[1.6fr_1fr]">
        {/* Progress + trend */}
        <Card className="p-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm text-muted">Current</p>
              <p className="font-display text-display-md tabular text-ink">{valueLabel(goal.currentValue, goal.unit)}</p>
            </div>
            {goal.targetValue != null && (
              <div className="text-right">
                <p className="text-sm text-muted">Target</p>
                <p className="font-display text-2xl tabular text-ink-soft">{valueLabel(goal.targetValue, goal.unit)}</p>
              </div>
            )}
          </div>

          {goal.progressPercent != null && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs font-medium text-muted">
                <span>{valueLabel(goal.startValue, goal.unit)}</span>
                <span>{goal.progressPercent}%</span>
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded-pill bg-surface">
                <div className="h-full rounded-pill bg-accent transition-[width]" style={{ width: `${goal.progressPercent}%` }} />
              </div>
            </div>
          )}

          <Trend checkins={checkins} target={goal.targetValue} unit={goal.unit} />
        </Card>

        {/* Status / details */}
        <Card className="flex flex-col gap-3 p-5">
          <InfoRow label="Started" value={formatDate(goal.startedOn)} />
          {goal.status === 'ACTIVE' && <InfoRow label="Next check-in" value={formatDate(goal.nextCheckInOn)} />}
          {goal.reachedOn && <InfoRow label="Reached" value={formatDate(goal.reachedOn)} />}
          <InfoRow label="Check-ins" value={String(goal.checkinCount)} />

          <div className="mt-1 flex flex-wrap gap-2 border-t border-line pt-3">
            {goal.status === 'ACTIVE' && (
              <Button size="sm" variant="outline" onClick={() => setStatus('PAUSED')}>Pause</Button>
            )}
            {(goal.status === 'PAUSED' || goal.status === 'ARCHIVED') && (
              <Button size="sm" variant="outline" onClick={() => setStatus('ACTIVE')}>
                {goal.status === 'ARCHIVED' ? 'Reactivate' : 'Resume'}
              </Button>
            )}
            {goal.status === 'ACTIVE' && goal.targetValue != null && (
              <Button size="sm" variant="outline" onClick={() => setStatus('REACHED')}>Mark reached</Button>
            )}
            {goal.status === 'REACHED' && (
              <Button size="sm" variant="outline" onClick={() => setStatus('ACTIVE')}>Reopen</Button>
            )}
            {goal.status !== 'ARCHIVED' && (
              <Button size="sm" variant="ghost" onClick={onArchive} className="text-bad-ink hover:bg-bad-soft">Archive</Button>
            )}
          </div>
        </Card>
      </div>

      <BeforeAfter checkins={checkins} unit={goal.unit} />

      {/* Check-in history */}
      <Card className="mt-3 overflow-hidden p-0">
        <div className="border-b border-line px-5 py-3">
          <h2 className="font-display text-lg font-semibold text-ink">Check-ins</h2>
        </div>
        {checkins.length ? (
          <ul className="divide-y divide-line">
            {checkins.map((c) => (
              <CheckinRow key={c.id} checkin={c} unit={goal.unit} onDeleted={reload} goalId={goalId} confirm={confirm} />
            ))}
          </ul>
        ) : (
          <p className="px-5 py-8 text-center text-sm text-muted">No check-ins yet — add the first measurement.</p>
        )}
      </Card>

      {checkinOpen && (
        <CheckinModal
          goalId={goalId}
          unit={goal.unit}
          onClose={() => setCheckinOpen(false)}
          onDone={() => {
            setCheckinOpen(false);
            reload();
          }}
        />
      )}
      {editOpen && (
        <EditGoalModal
          goal={goal}
          onClose={() => setEditOpen(false)}
          onDone={() => {
            setEditOpen(false);
            reload();
          }}
        />
      )}
    </section>
  );
}

/** Oldest→newest line of measurements with an optional dashed target line. */
function Trend({ checkins, target, unit }: { checkins: GoalCheckin[]; target: number | null; unit: string }) {
  const pts = useMemo(() => [...checkins].reverse().map((c) => c.value), [checkins]);
  if (pts.length < 2) {
    return <p className="mt-4 text-xs text-muted">Add at least two check-ins to see the trend.</p>;
  }
  const W = 600;
  const H = 150;
  const all = target != null ? [...pts, target] : pts;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const pad = (max - min) * 0.12 || 1;
  const lo = min - pad;
  const hi = max + pad;
  const x = (i: number) => (i / (pts.length - 1)) * W;
  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;
  const line = pts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');

  return (
    <div className="mt-5">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-36 w-full" role="img" aria-label="Progress trend">
        {target != null && (
          <line x1={0} y1={y(target)} x2={W} y2={y(target)} stroke="currentColor" strokeWidth={1.5}
            strokeDasharray="6 6" className="text-accent-strong/60" />
        )}
        <path d={line} fill="none" stroke="currentColor" strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" className="text-ink" />
        {pts.map((v, i) => (
          <circle key={i} cx={x(i)} cy={y(v)} r={4} className="fill-accent stroke-ink" strokeWidth={1.5} />
        ))}
      </svg>
      <div className="mt-1 flex items-center justify-between text-xs text-muted">
        <span>{formatDate([...checkins].reverse()[0].measuredOn)}</span>
        {target != null && <span className="text-accent-strong">target {valueLabel(target, unit)}</span>}
        <span>{formatDate(checkins[0].measuredOn)}</span>
      </div>
    </div>
  );
}

/** Side-by-side first-photo vs latest-photo, the satisfying "look how far you've come". */
function BeforeAfter({ checkins, unit }: { checkins: GoalCheckin[]; unit: string }) {
  const withPhoto = checkins.filter((c) => c.photoUrl);
  if (withPhoto.length < 2) return null;
  const before = withPhoto[withPhoto.length - 1]; // oldest (list is newest-first)
  const after = withPhoto[0]; // newest

  return (
    <Card className="mt-3 p-5">
      <h2 className="font-display text-lg font-semibold text-ink">Before &amp; after</h2>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {[
          { label: 'Before', c: before },
          { label: 'After', c: after },
        ].map(({ label, c }) => (
          <figure key={label} className="overflow-hidden rounded-card bg-surface">
            <img src={mediaUrl(c.photoUrl)} alt={`${label} photo`} className="aspect-square w-full object-cover" />
            <figcaption className="flex items-center justify-between px-3 py-2 text-xs">
              <span className="font-medium text-ink-soft">{label}</span>
              <span className="text-muted tabular">{valueLabel(c.value, unit)} · {formatDate(c.measuredOn)}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Card>
  );
}

function CheckinRow({
  checkin,
  unit,
  goalId,
  onDeleted,
  confirm,
}: {
  checkin: GoalCheckin;
  unit: string;
  goalId: string;
  onDeleted: () => void;
  confirm: ReturnType<typeof useConfirm>;
}) {
  async function remove() {
    const ok = await confirm({ title: 'Delete this check-in?', description: 'This can’t be undone.', confirmLabel: 'Delete', tone: 'danger' });
    if (!ok) return;
    await deleteCheckin(goalId, checkin.id);
    onDeleted();
  }
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      {checkin.photoUrl && (
        <img src={mediaUrl(checkin.photoUrl)} alt="" className="size-12 shrink-0 rounded-control object-cover" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink tabular">{valueLabel(checkin.value, unit)}</p>
        <p className="text-xs text-muted">
          {formatDate(checkin.measuredOn)}
          {checkin.note ? ` · ${checkin.note}` : ''}
          {checkin.recordedByName ? ` · ${checkin.recordedByName}` : ''}
        </p>
      </div>
      <button onClick={remove} aria-label="Delete check-in" className="shrink-0 text-muted hover:text-bad-ink">
        <Trash2 className="size-4" aria-hidden />
      </button>
    </li>
  );
}

function CheckinModal({ goalId, unit, onClose, onDone }: { goalId: string; unit: string; onClose: () => void; onDone: () => void }) {
  const [value, setValue] = useState('');
  const [measuredOn, setMeasuredOn] = useState(todayIso);
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function pickPhoto(file: File | null) {
    setPhoto(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (value.trim() === '' || Number.isNaN(Number(value))) return setError('Enter a measurement.');
    setSaving(true);
    try {
      const res = await recordCheckin(goalId, { value: Number(value), measuredOn, note: note.trim() || undefined });
      if (photo) {
        // The new check-in is the newest (detail returns newest-first).
        await uploadCheckinPhoto(goalId, res.checkins[0].id, photo);
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the check-in.');
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add check-in"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={submit} loading={saving}>{!saving && <Check className="size-4" aria-hidden />} Save</Button>
        </div>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input label={`Measurement (${unit})`} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        <Input label="Measured on" type="date" value={measuredOn} onChange={(e) => setMeasuredOn(e.target.value)} />
        <Textarea label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />

        <div>
          <span className="text-sm font-medium text-ink-soft">Progress photo (optional)</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => pickPhoto(e.target.files?.[0] ?? null)}
          />
          {photoPreview ? (
            <div className="mt-2 flex items-center gap-3">
              <img src={photoPreview} alt="" className="size-16 rounded-control object-cover" />
              <Button type="button" size="sm" variant="ghost" onClick={() => pickPhoto(null)}>Remove</Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="mt-2 flex h-16 w-full items-center justify-center gap-2 rounded-control border border-dashed border-line-strong text-sm font-medium text-muted hover:bg-surface"
            >
              <ImagePlus className="size-5" aria-hidden /> Add a photo
            </button>
          )}
        </div>

        {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      </form>
    </Modal>
  );
}

function EditGoalModal({ goal, onClose, onDone }: { goal: GoalDetail['goal']; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState(goal.title);
  const [targetValue, setTargetValue] = useState(goal.targetValue == null ? '' : String(goal.targetValue));
  const [cadenceDays, setCadenceDays] = useState(String(goal.cadenceDays));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const cadence = parseInt(cadenceDays, 10);
    if (!title.trim()) return setError('Give the goal a name.');
    if (!(cadence >= 1)) return setError('Check-in frequency must be at least 1 day.');
    setSaving(true);
    try {
      await updateGoal(goal.id, {
        title: title.trim(),
        targetValue: targetValue === '' ? null : Number(targetValue),
        cadenceDays: cadence,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the goal.');
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit goal"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={submit} loading={saving}>{!saving && <Check className="size-4" aria-hidden />} Save</Button>
        </div>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input label="Goal name" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input label={`Target (${goal.unit}, optional)`} inputMode="decimal" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} hint="Leave blank to just track the trend." />
        <Input label="Check in every (days)" inputMode="numeric" value={cadenceDays} onChange={(e) => setCadenceDays(e.target.value)} hint={Number(cadenceDays) >= 1 ? cadenceLabel(Number(cadenceDays)) : undefined} />
        {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      </form>
    </Modal>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted">{label}</span>
      <span className="font-medium text-ink tabular">{value}</span>
    </div>
  );
}

function BackLink({ onClick, label = 'Back' }: { onClick: () => void; label?: string }) {
  return (
    <button onClick={onClick} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
      <ArrowLeft className="size-4" aria-hidden /> {label}
    </button>
  );
}
